import React, { useEffect, useState } from 'react'
import * as R from 'ramda'
import { Order } from './model-order'
import { Restaurant } from './model-restaurant'
import { Channel } from './model-channel'

import { Panel } from './fragments/comp/panel'

import moment, { Moment } from 'moment'

interface IntegrationInfoProps {
  order: Order
  channel: Channel
  restaurant: Restaurant
}

export default function IntegrationInfo(props: IntegrationInfoProps) {
  const [curTime, setCurTime] = useState<string>(formatMomentTime(moment().utcOffset(props.restaurant.timezone.offset)))

  useEffect(() => {
    const tick = setInterval(() => {
      const time = moment().utcOffset(props.restaurant.timezone.offset)
      setCurTime(formatMomentTime(time).replace(/:/, time.seconds() % 2 == 0 ? ':' : ' '))
    }, 1000)
    return () => clearInterval(tick)
  }, [])

  const serviceLabel = props.order.carts.settings.service.label

  const service =
    props.restaurant.services.find((s) => s.id == props.order.carts.settings.service.id) ||
    R.head(props.restaurant.services)
  const contact = service && service.contact ? service.contact : null

  const cpURL =
    process.env.NODE_ENV == 'production'
      ? 'http://restaurants.zuppler.com'
      : 'http://restaurants.biznettechnologies.com'

  const chefURL = process.env.NODE_ENV == 'production' ? 'https://chef.zuppler.com' : 'https://zuppler-chef.netlify.app'

  const orderDay = moment(props.order.carts.settings.time.value)
  const showTodayHours = !moment().isSame(props.order.carts.settings.time.value, 'day')

  return (
    <div className="row">
      <div className="col-12">
        <Panel id="restaurant" label="Restaurant">
          <li className="list-group-item">
            <div className="src-head">
              <div className="src-name">
                {props.restaurant.name}
                <span
                  className={
                    props.restaurant.settings.pause_online_ordering
                      ? 'src-chip src-chip-danger'
                      : 'src-chip src-chip-success'
                  }>
                  {props.restaurant.settings.pause_online_ordering ? 'Paused' : 'Live'}
                </span>
              </div>
              <div className="src-time">
                {curTime} <span className="src-tz">({props.restaurant.timezone.name})</span>
              </div>
            </div>
            <div className="src-actions">
              <a target="_blank" href={`${chefURL}/#/onboarding/restaurant/${props.restaurant.id}`} rel="noreferrer">
                Zuppler Chef ↗
              </a>
              <a target="_blank" href={`${cpURL}/restaurants/${props.restaurant.permalink}`} rel="noreferrer">
                Control Panel ↗ <span className="src-deprecated">(deprecated)</span>
              </a>
            </div>
          </li>

          <li className="list-group-item">
            <div className="src-section-title">Hours of operation</div>
              <div className="src-grid">
                {showTodayHours && (
                  <>
                    <span className="src-k">Today</span>
                    <span className="src-v">
                      <HoursOfOperations
                        refDate={moment()}
                        zoneOffset={props.restaurant.timezone.offset}
                        data={props.restaurant.hours_of_operation}
                      />
                    </span>
                  </>
                )}
                <span className="src-k">{orderDay.format('ddd, MMM D')}</span>
                <span className="src-v">
                  <HoursOfOperations
                    refDate={orderDay}
                    zoneOffset={props.restaurant.timezone.offset}
                    data={props.restaurant.hours_of_operation}
                  />
                </span>
              </div>
            </li>

          {contact && (
            <li className="list-group-item">
              <div className="src-section-title">
                {serviceLabel} contact
              </div>
              <div className="src-grid">
                <span className="src-k">Name</span>
                <span className="src-v">{contact.name}</span>
                {contact.phone && (
                  <>
                    <span className="src-k">Phone</span>
                    <span className="src-v">
                      <a href={`tel:${contact.phone}`}>{contact.phone}</a>
                    </span>
                  </>
                )}
                {contact.email && (
                  <>
                    <span className="src-k">Email</span>
                    <span className="src-v">
                      <a href={`mailto:${contact.email}?subject=${encodeURIComponent(`Order ${props.order.uuid}`)}`}>
                        {contact.email}
                      </a>
                    </span>
                  </>
                )}
              </div>
            </li>
          )}
        </Panel>

        <Panel id="order-source" label="Order Source">
          <li className="list-group-item">
            <div className="src-grid">
              <span className="src-k">Channel</span>
              <span className="src-v">{props.channel.name}</span>
              <span className="src-k">URL</span>
              <span className="src-v">
                <a href={props.channel.url} target="_blank" rel="noreferrer" className="src-break">
                  {props.channel.url}
                </a>
              </span>
              <span className="src-k">Permalink</span>
              <span className="src-v">{props.channel.permalink}</span>
            </div>
            <div className="src-actions">
              <a target="_blank" href={`${cpURL}/channels/${props.channel.permalink}`} rel="noreferrer">
                Configure channel ↗
              </a>
            </div>
            {!props.order.carts.integration_url
              .replace(/https?:\/\//, '')
              .includes(props.channel.url.replace(/https?:\/\//, '')) && (
              <div className="src-alert">
                Order came from an invalid URL. Please review the channel configuration.
              </div>
            )}
          </li>

          <li className="list-group-item">
            <div className="src-section-title">Integration</div>
            <div className="src-grid">
              <span className="src-k">Order URL</span>
              <span className="src-v">
                <a href={props.order.carts.integration_url} target="_blank" rel="noreferrer" className="src-break">
                  {props.order.carts.integration_url}
                </a>
              </span>
              <span className="src-k">Remote ID</span>
              <span className="src-v src-code">{props.channel.integrations[0].remote_id}</span>
            </div>
          </li>
        </Panel>
      </div>
    </div>
  )
}

function HoursOfOperations(props: { refDate: Moment; zoneOffset: number; data: number[][][] }) {
  const dow = props.refDate.day()
  const intervals = R.filter(([start, stop]) => start < stop && start != 0, props.data[dow])
  const joinedIntervals = R.reduce(
    (prev, [start, stop]) => {
      if (prev && prev.length > 0) {
        const [pStart, pStop] = R.last(prev) as number[]
        if (pStop == start) {
          return R.append([pStart, stop], R.take(prev.length - 1, prev))
        } else {
          return R.append([start, stop], prev)
        }
      } else {
        return [start, stop]
      }
    },
    [R.head(intervals)],
    R.tail(intervals)
  )

  if (joinedIntervals.length > 0) {
    const joinedMomentIntervals = joinedIntervals
      .filter((n?: number[]) => !!n && n.length == 2)
      .map(([start, stop]: number[]) => [toMoment(props.zoneOffset, start), toMoment(props.zoneOffset, stop)])
      .map(([start, stop]: Moment[]) => [formatMomentTime(start), formatMomentTime(stop, start)])
      .map(([start, stop]: string[]) => `${start} - ${stop}`)
      .join('; ')

    return <>{joinedMomentIntervals || 'Closed'}</>
  } else {
    return <>Closed</>
  }
}

const formatMomentTime = (m: Moment, ref?: Moment): string =>
  ref && ref.isSameOrBefore(m) ? m.format('LT') + ' (next day)' : m.format('LT')

const toMoment = (zoneOffset: number, startMin: number) => {
  const m = moment()
  m.utcOffset(zoneOffset)
  m.hours(Math.floor(startMin % 1440) / 60)
  m.minutes(startMin % 60)
  return m
}